"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Z_BOOK_STAY_TAB } from "@/components/layout/zIndex";

// Shared visual language with the existing accent-colored CTAs (Plan a Trip,
// Concierge, search submit): bg-[var(--cta)] + dark text already satisfies
// "gold/amber in dark mode, warm ochre in light mode" via the theme's own
// --accent token, no separate light/dark branching needed.
const CTA_BASE = `${Z_BOOK_STAY_TAB} flex items-center justify-center bg-[var(--cta)] text-[#2b2b2b] no-underline shadow-[0_8px_24px_rgba(0,0,0,0.25)] transition-all duration-150 hover:bg-[var(--cta-bright)] active:brightness-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2b2b2b]`;

/**
 * "Book Your Resort" shortcut to the resorts directory — an ordinary link at
 * every size: one tap, click or Enter, no reveal step and no timeout.
 *
 * - 1360px and up: the full tab. Page content only moves far enough from the
 *   right edge to clear it (~54px with hover) once the 1280px container can
 *   centre, at about 1344px.
 * - 641–1359px: a slim 24px tab — the minimum touch-target size, and exactly
 *   the page's side padding there, so it sits flush beside content without
 *   covering it.
 * - Phones (≤640px): not shown. The side padding is only 16px, so any tab
 *   large enough to tap would sit over content; Resorts is one tap away in
 *   the mobile menu instead.
 * - Not shown on /stays or a resort page: it would link to the page the
 *   visitor is already on, or pull them away from the booking card of the
 *   resort they are viewing.
 */
export function StayBookingTab() {
  const pathname = usePathname() ?? "";
  if (pathname === "/stays" || pathname.startsWith("/stays/")) return null;

  return (
    <>
      <Link
        href="/stays"
        aria-label="Book your resort"
        className={`hidden min-[1360px]:flex fixed top-1/2 -translate-y-1/2 rounded-l-lg border border-r-0 border-[var(--border-subtle)] px-3 py-9 hover:px-4 ${CTA_BASE}`}
        style={{ right: "env(safe-area-inset-right, 0px)" }}
      >
        <span className="[writing-mode:vertical-rl] rotate-180 whitespace-nowrap text-[13px] font-bold uppercase tracking-[0.16em]">
          Book Your Resort
        </span>
      </Link>

      <Link
        href="/stays"
        aria-label="Book your resort"
        className={`hidden min-[641px]:flex min-[1360px]:hidden fixed top-1/2 -translate-y-1/2 w-6 py-5 rounded-l-md border border-r-0 border-[var(--border-subtle)] ${CTA_BASE}`}
        style={{ right: "env(safe-area-inset-right, 0px)" }}
      >
        <span className="[writing-mode:vertical-rl] rotate-180 whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.12em]">
          Book Your Resort
        </span>
      </Link>
    </>
  );
}
